class SearchModal extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.details = this.querySelector('details');
        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
        this.closeButton = this.querySelector('.search-modal__close-button-link');
        this.resetButton = this.querySelector('.reset__button');
        this.isOpen = false;

        this.setupEventListeners();
        console.log('SearchModal initialized');
    }

    setupEventListeners() {
        // Global click delegation for all search buttons in header, drawer, and bottom bar
        document.addEventListener('click', (e) => {
            const searchBtn = e.target.closest('.header__search-icon, .js-search-trigger, [data-search-trigger]');
            if (searchBtn && !searchBtn.closest('form') && !searchBtn.classList.contains('search__button')) {
                e.preventDefault();
                this.open();
            }
        });

        if (this.closeButton) {
            this.closeButton.addEventListener('click', () => this.close());
        }

        if (this.resetButton) {
            this.resetButton.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.input.value = '';
                this.input.focus();
                this.onInput(event);
            });
        }

        if (this.input) {
            this.input.addEventListener('input', this.debounce((event) => {
                this.onInput(event);
            }, 300).bind(this));
        }

        // Close on escape
        document.addEventListener('keyup', (event) => {
            if (event.code && event.code.toUpperCase() === 'ESCAPE' && this.isOpen) this.close();
        });

        // Close on click outside (backdrop)
        this.addEventListener('click', (event) => {
            if (event.target === this) this.close();
        });
    }

    open() {
        this.isOpen = true;
        this.removeAttribute('hidden');
        this.setAttribute('open', '');
        this.style.display = 'flex';
        setTimeout(() => {
            this.classList.add('open');
            if (this.input) this.input.focus();
        }, 30);
        document.body.style.overflow = 'hidden';
    }

    close() {
        this.isOpen = false;
        this.removeAttribute('open');
        this.classList.remove('open');
        document.body.style.overflow = '';
        setTimeout(() => {
            if (!this.isOpen) {
                this.style.display = 'none';
                this.closeResults();
            }
        }, 300);
    }

    onInput(event) {
        const searchTerm = this.input.value.trim();

        if (this.resetButton) {
            if (searchTerm.length > 0) {
                this.resetButton.classList.remove('hidden');
            } else {
                this.resetButton.classList.add('hidden');
            }
        }

        if (!searchTerm.length) {
            this.closeResults();
            return;
        }

        this.getSearchResults(searchTerm);
    }

    getSearchResults(searchTerm) {
        const queryKey = searchTerm.replace(" ", "-").toLowerCase();
        this.setLiveRegionLoadingState();

        // Show loader
        const loadingState = this.querySelector('.predictive-search__loading-state');
        if (loadingState) loadingState.style.display = 'flex';
        this.predictiveSearchResults.style.display = 'block';

        // Use Shopify Predictive Search API
        fetch(`/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,page,article,query&resources[limit]=5&section_id=predictive-search`)
            .then((response) => {
                if (!response.ok) {
                    var error = new Error(response.status);
                    this.closeResults();
                    throw error;
                }

                return response.json();
            })
            .then((response) => {
                // Since we are using the JSON API, we need to build the HTML manually OR use the section rendering API.
                // The URL above ?section_id=predictive-search implies we have a section called predictive-search. 
                // IF we don't have that section, we should parse the JSON.
                // Let's assume we parse JSON for now as it's cleaner than creating a whole new section file just for this if not requested,
                // BUT rendering a section is often standard Shopify practice.
                // HOWEVER, to be self-contained in assets, let's parse JSON.

                // Wait, the query above uses `search/suggest.json`. 
                // If we want HTML, we can use `search/suggest?q=...&section_id=...` (no .json).
                // Let's try to fetch JSON and verify results for now, or just render HTML from JS.

                this.renderSearchResults(response.resources.results);
            })
            .catch((error) => {
                this.closeResults();
                throw error;
            })
            .finally(() => {
                if (loadingState) loadingState.style.display = 'none';
            });
    }



    renderSearchResults(results) {
        this.predictiveSearchResults.innerHTML = '';
        const { products, queries, pages, articles } = results;

        const hasResults = (products && products.length) || (queries && queries.length) || (pages && pages.length) || (articles && articles.length);

        if (!hasResults) {
            this.predictiveSearchResults.innerHTML = '<p style="padding:1rem;">No results found.</p>';
            this.predictiveSearchResults.style.display = 'block';
            return;
        }

        const container = document.createElement('div');
        container.classList.add('predictive-search__results-groups-wrapper');

        // 1. Products (First Column)
        if (products && products.length > 0) {
            const group = this.createGroup('Products');
            products.forEach(product => {
                group.querySelector('ul').appendChild(this.createProductItem(product));
            });
            container.appendChild(group);
        }

        // 2. Suggestions (Queries)
        if (queries && queries.length > 0) {
            const group = this.createGroup('Suggestions');
            queries.forEach(query => {
                group.querySelector('ul').appendChild(this.createSuggestionItem(query));
            });
            container.appendChild(group);
        }

        // 3. Pages & Articles
        if ((pages && pages.length > 0) || (articles && articles.length > 0)) {
            const group = this.createGroup('Pages & Articles');
            if (pages) {
                pages.forEach(page => {
                    group.querySelector('ul').appendChild(this.createPageItem(page));
                });
            }
            if (articles) {
                articles.forEach(article => {
                    group.querySelector('ul').appendChild(this.createArticleItem(article));
                });
            }
            container.appendChild(group);
        }

        this.predictiveSearchResults.appendChild(container);
        this.predictiveSearchResults.style.display = 'block';
    }

    createGroup(title) {
        const group = document.createElement('div');
        group.classList.add('predictive-search__results-group');
        group.innerHTML = `
            <h3 class="predictive-search__heading">${title}</h3>
            <ul class="predictive-search__results-list" role="listbox"></ul>
        `;
        return group;
    }

    createSuggestionItem(query) {
        const item = document.createElement('li');
        item.classList.add('predictive-search__list-item');
        item.innerHTML = `
            <a href="${query.url}" class="predictive-search__item predictive-search__item--term" role="option">
                <span class="predictive-search__item-content">
                    <span class="predictive-search__item-heading" aria-label="${query.text}">${query.styled_text}</span>
                </span>
            </a>
        `;
        return item;
    }

    createPageItem(page) {
        const item = document.createElement('li');
        item.classList.add('predictive-search__list-item');
        item.innerHTML = `
            <a href="${page.url}" class="predictive-search__item" role="option">
                <span class="predictive-search__item-content">
                    <span class="predictive-search__item-heading">${page.title}</span>
                </span>
            </a>
        `;
        return item;
    }

    createArticleItem(article) {
        const item = document.createElement('li');
        item.classList.add('predictive-search__list-item');
        item.innerHTML = `
            <a href="${article.url}" class="predictive-search__item" role="option">
                <span class="predictive-search__item-content">
                    <span class="predictive-search__item-heading">${article.title}</span>
                </span>
            </a>
        `;
        return item;
    }

    createProductItem(product) {
        const item = document.createElement('li');
        item.classList.add('predictive-search__list-item');

        const imageHtml = product.image ? `<img class="predictive-search__image" src="${product.image}" alt="${product.title}" width="50" height="50">` : '';

        item.innerHTML = `
            <a href="${product.url}" class="predictive-search__item" role="option">
                ${imageHtml}
                <div class="predictive-search__item-content">
                    <span class="predictive-search__item-heading">${product.title}</span>
                    <span class="predictive-search__item-price">${product.price}</span>
                </div>
            </a>
        `;
        return item;
    }

    setLiveRegionLoadingState() {
        this.predictiveSearchResults.setAttribute('aria-busy', 'true');
    }

    setLiveRegionResults() {
        this.predictiveSearchResults.removeAttribute('aria-busy');
    }

    closeResults() {
        this.predictiveSearchResults.style.display = 'none';
        this.predictiveSearchResults.innerHTML = '';
    }

    debounce(fn, wait) {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
        };
    }
}

customElements.define('search-modal', SearchModal);

class MainPredictiveSearch extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
        this.resetButton = this.querySelector('.reset__button');

        this.setupEventListeners();
    }

    setupEventListeners() {
        if (this.resetButton) {
            this.resetButton.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.input.value = '';
                this.input.focus();
                this.onInput(event);
            });
        }

        if (this.input) {
            this.input.addEventListener('input', this.debounce((event) => {
                this.onInput(event);
            }, 300).bind(this));
        }
    }

    onInput(event) {
        const searchTerm = this.input.value.trim();

        if (this.resetButton) {
            if (searchTerm.length > 0) {
                this.resetButton.classList.remove('hidden');
            } else {
                this.resetButton.classList.add('hidden');
            }
        }

        if (!searchTerm.length) {
            this.closeResults();
            return;
        }

        this.getSearchResults(searchTerm);
    }

    getSearchResults(searchTerm) {
        this.setLiveRegionLoadingState();

        const loadingState = this.querySelector('.predictive-search__loading-state');
        if (loadingState) loadingState.style.display = 'flex';
        this.predictiveSearchResults.style.display = 'block';

        fetch(`/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,page,article,query&resources[limit]=5&section_id=predictive-search`)
            .then((response) => {
                if (!response.ok) {
                    var error = new Error(response.status);
                    this.closeResults();
                    throw error;
                }
                return response.json();
            })
            .then((response) => {
                this.renderSearchResults(response.resources.results);
            })
            .catch((error) => {
                this.closeResults();
                throw error;
            })
            .finally(() => {
                if (loadingState) loadingState.style.display = 'none';
            });
    }

    renderSearchResults(results) {
        this.predictiveSearchResults.innerHTML = '';
        const { products, queries, pages, articles } = results;

        const hasResults = (products && products.length) || (queries && queries.length) || (pages && pages.length) || (articles && articles.length);

        if (!hasResults) {
            this.predictiveSearchResults.innerHTML = '<p style="padding:1rem;">No results found.</p>';
            this.predictiveSearchResults.style.display = 'block';
            return;
        }

        const container = document.createElement('div');
        container.classList.add('predictive-search__results-groups-wrapper');

        if (products && products.length > 0) {
            const group = this.createGroup('Products');
            products.forEach(product => {
                group.querySelector('ul').appendChild(this.createProductItem(product));
            });
            container.appendChild(group);
        }

        if (queries && queries.length > 0) {
            const group = this.createGroup('Suggestions');
            queries.forEach(query => {
                group.querySelector('ul').appendChild(this.createSuggestionItem(query));
            });
            container.appendChild(group);
        }

        if ((pages && pages.length > 0) || (articles && articles.length > 0)) {
            const group = this.createGroup('Pages & Articles');
            if (pages) {
                pages.forEach(page => {
                    group.querySelector('ul').appendChild(this.createPageItem(page));
                });
            }
            if (articles) {
                articles.forEach(article => {
                    group.querySelector('ul').appendChild(this.createArticleItem(article));
                });
            }
            container.appendChild(group);
        }

        this.predictiveSearchResults.appendChild(container);
        this.predictiveSearchResults.style.display = 'block';
    }

    createGroup(title) {
        const group = document.createElement('div');
        group.classList.add('predictive-search__results-group');
        group.innerHTML = `
            <h3 class="predictive-search__heading">${title}</h3>
            <ul class="predictive-search__results-list" role="listbox"></ul>
        `;
        return group;
    }

    createSuggestionItem(query) {
        const item = document.createElement('li');
        item.classList.add('predictive-search__list-item');
        item.innerHTML = `
            <a href="${query.url}" class="predictive-search__item predictive-search__item--term" role="option">
                <span class="predictive-search__item-content">
                    <span class="predictive-search__item-heading" aria-label="${query.text}">${query.styled_text}</span>
                </span>
            </a>
        `;
        return item;
    }

    createPageItem(page) {
        const item = document.createElement('li');
        item.classList.add('predictive-search__list-item');
        item.innerHTML = `
            <a href="${page.url}" class="predictive-search__item" role="option">
                <span class="predictive-search__item-content">
                    <span class="predictive-search__item-heading">${page.title}</span>
                </span>
            </a>
        `;
        return item;
    }

    createArticleItem(article) {
        const item = document.createElement('li');
        item.classList.add('predictive-search__list-item');
        item.innerHTML = `
            <a href="${article.url}" class="predictive-search__item" role="option">
                <span class="predictive-search__item-content">
                    <span class="predictive-search__item-heading">${article.title}</span>
                </span>
            </a>
        `;
        return item;
    }

    createProductItem(product) {
        const item = document.createElement('li');
        item.classList.add('predictive-search__list-item');

        const imageHtml = product.image ? `<img class="predictive-search__image" src="${product.image}" alt="${product.title}" width="50" height="50">` : '';

        item.innerHTML = `
            <a href="${product.url}" class="predictive-search__item" role="option">
                ${imageHtml}
                <div class="predictive-search__item-content">
                    <span class="predictive-search__item-heading">${product.title}</span>
                    <span class="predictive-search__item-price">${product.price}</span>
                </div>
            </a>
        `;
        return item;
    }

    setLiveRegionLoadingState() {
        this.predictiveSearchResults.setAttribute('aria-busy', 'true');
    }

    closeResults() {
        this.predictiveSearchResults.style.display = 'none';
        this.predictiveSearchResults.innerHTML = '';
    }

    debounce(fn, wait) {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
        };
    }
}

customElements.define('main-predictive-search', MainPredictiveSearch);
